/**
 * BENCH MODE y las vistas de la Fase D — la parte PURA.
 *
 * Acá vive todo lo que se puede probar sin React, sin DOM y sin red: cómo se
 * dibuja la cancha según de qué lado está sentado el DT, qué cambio es legal
 * (regla 15.6), qué dice la pantalla cuando se corta la red y qué se guarda en
 * el navegador para que un F5 en medio del set no deje al banco en blanco.
 *
 * ## LA DIFERENCIA CON EL BENCH DEL COMPETIDOR, DICHA SIN ADORNOS
 *
 * El de VolleyStation es una web servida por la laptop del estadístico: el DT
 * escanea un QR y entra por LAN. Eso tiene una ventaja que nosotros **no**
 * tenemos: sobrevive un corte de internet, porque nunca sale del estadio.
 * Exige, eso sí, estar en la misma red — que en la práctica quiere decir
 * llevar router propio, porque las redes de estadio no dejan pasar tráfico
 * entre clientes.
 *
 * El nuestro va contra el API, así que **sirve también para el staff remoto**
 * (el analista del club mirando desde la sede, el DT suspendido en la tribuna)
 * y no exige infraestructura en cancha. El precio es real: sin internet, no
 * hay bench. No lo tapamos — lo diseñamos:
 *
 *  1. **La pantalla nunca se vacía.** El último estado conocido queda en
 *     `localStorage` y se dibuja igual, con su hora, marcado como viejo.
 *  2. **El estado del enlace es un dato de primera clase**, no un ícono: hay un
 *     cartel que dice *"sin red, el partido sigue"* con la hora del último
 *     dato, porque un marcador congelado sin aviso es peor que no tener nada.
 *  3. **Los pedidos NO se encolan a ciegas.** Un cambio pedido hace tres
 *     minutos que llega ahora es un cambio equivocado. Se reintenta unos
 *     segundos (un parpadeo de red no es una caída) y después la pantalla dice
 *     lo que el reglamento ya dice: **pedilo con la seña**, que es el pedido
 *     oficial de verdad (regla 15.5.1 y, para el challenge, VCS art. 21).
 */
import type { MatchRole, TeamSide } from '@scout/core';
import type { LiveStatus } from './live';
import { defaultKeyValueStore, type KeyValueStore } from './settings';

// ---------------------------------------------------------------------------
// La forma de lo que devuelve el API (`GET /api/matches/:id/bench`)
//
// Se declara acá copiando la forma, igual que `SeasonReport` y `Clip` en
// `api-client.ts`: el portal consume JSON y no importa el módulo del API.
// ---------------------------------------------------------------------------

export interface BenchPlayer {
  number: number;
  name?: string;
  role?: string;
  isCaptain?: boolean;
  libero?: boolean;
}

export interface BenchTeamMeta {
  name: string;
  code?: string;
  coach?: string;
  players: BenchPlayer[];
  liberos: number[];
}

/** Un ciclo de sustitución de la regla 15.6 (ver `substitutionOptions`). */
export interface SubstitutionSlot {
  starter: number;
  substitute: number;
  state: 'OUT' | 'RETURNED';
}

export interface SubstitutionGate {
  used: number;
  limit: number;
  remaining: number;
  warn: boolean;
  blocked: boolean;
}

export interface BenchTeamState {
  score: number;
  timeoutsUsed: number;
  timeoutsLimit: number;
  rotation: number[];
  onCourt: number[];
  substitutions: SubstitutionGate;
  exceptionalSubsUsed: number;
  slots: SubstitutionSlot[];
  challengesRemaining: number;
  challengesFailed: number;
}

export interface BenchRally {
  set: number;
  index: number;
  home: number;
  away: number;
  server: TeamSide | null;
  winner: TeamSide | null;
  reason: string | null;
  startedAtNs: number | null;
  pointNs: number | null;
  closed: boolean;
}

export type IntentKind = 'lineup' | 'substitution' | 'timeout' | 'challenge';
export type IntentState = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'CANCELLED';

export interface BenchIntent {
  id: string;
  matchId: string;
  kind: IntentKind;
  team: TeamSide;
  payload: Record<string, unknown>;
  requestedBy: { subjectId: string; role: MatchRole; holderName: string };
  requestedAt: string;
  requestedAtNs?: number;
  state: IntentState;
  resolvedAt?: string;
  resolvedBy?: string;
  reason?: string;
  eventId?: string;
  version: number;
  /** Instante en que el pedido deja de poder aceptarse (lo calcula el server). */
  staleAt: string;
  stale: boolean;
}

export interface BenchSnapshot {
  matchId: string;
  generatedAt: string;
  lastSeq: number;
  name: string;
  competition?: string;
  venue?: string;
  phase: string;
  sheetPhase: string;
  sheetClosed: boolean;
  currentSet: number;
  setsWon: Record<TeamSide, number>;
  finishedSets: Array<{ setNumber: number; home: number; away: number; wonBy: TeamSide }>;
  servingTeam: TeamSide | null;
  meta: Record<TeamSide, BenchTeamMeta>;
  teams: Record<TeamSide, BenchTeamState>;
  challenge: { open: boolean; windowClosesAtNs: number | null; pointInsertedAtNs: number | null };
  rallies: BenchRally[];
  intentVersion: number;
  intents: BenchIntent[];
}

// ---------------------------------------------------------------------------
// Qué vista le toca a cada credencial (§5.4)
// ---------------------------------------------------------------------------

/**
 * Las cinco pantallas de la topología del MVP. Son cinco VISTAS de la misma
 * proyección, no cinco pantallas distintas: lo que cambia es qué se dibuja y
 * qué se puede apretar, nunca qué se sabe del partido.
 */
export type BenchView =
  /** Tablet del banco: marcador, cancha, reportes y los cuatro pedidos. */
  | 'banco'
  /** Puesto de mesa: la bandeja de entrada de los pedidos. */
  | 'mesa'
  /** Árbitro (1º, 2º, challenge, reserva): sólo lectura, pedidos y rotaciones. */
  | 'arbitro'
  /** Monitor espejo del supervisor: todo, sin un solo botón. */
  | 'espejo'
  /** Pantalla de estadio: marcador gigante y nada más. */
  | 'tablero';

export const BENCH_VIEWS: readonly BenchView[] = [
  'banco',
  'mesa',
  'arbitro',
  'espejo',
  'tablero',
];

export function isBenchView(value: string): value is BenchView {
  return (BENCH_VIEWS as readonly string[]).includes(value);
}

/**
 * Vista por defecto de un rol de partido.
 *
 * El default de lo desconocido es `espejo` —lectura total, cero botones— a
 * propósito: una credencial cuyo rol no previmos tiene que poder mirar y no
 * tiene que poder tocar.
 */
export function viewForRole(role: string | null | undefined): BenchView {
  switch (role) {
    case 'team_delegate':
    case 'team_captain':
      return 'banco';
    case 'scorer':
    case 'assistant_scorer':
      return 'mesa';
    case 'referee1':
    case 'referee2':
    case 'challenge_referee':
    case 'reserve_referee':
    case 'line_judge':
      return 'arbitro';
    case 'scoreboard':
      return 'tablero';
    default:
      return 'espejo';
  }
}

/** ¿Esta vista pide cosas? (el banco, y sólo si la credencial lo permite) */
export function canRequest(view: BenchView, permissions: readonly string[]): boolean {
  return view === 'banco' && permissions.includes('intents:write');
}

/** ¿Esta vista atiende la cola? (la mesa) */
export function canResolve(view: BenchView, permissions: readonly string[]): boolean {
  return view === 'mesa' && permissions.includes('intents:resolve');
}

// ---------------------------------------------------------------------------
// La cancha, girada según dónde está sentado el DT
// ---------------------------------------------------------------------------

export interface CourtCell {
  /** Posición reglamentaria 1..6 (P1 es el que saca). */
  position: number;
  /** Camiseta, o `null` si todavía no hay formación cargada. */
  number: number | null;
}

/**
 * Rotación `[P1..P6]` → las dos filas de la cancha, tal como se ven.
 *
 * Sin girar, la red está ARRIBA y el que saca (P1) queda abajo a la derecha,
 * que es como se ve desde el banco propio:
 *
 *     P4  P3  P2      ← red
 *     P5  P6  P1      ← fondo, P1 saca
 *
 * `flipped` gira la cancha 180°, que es lo que hace falta cuando el cuerpo
 * técnico está sentado del otro lado: si no, el DT tiene que hacer la
 * transformación en la cabeza mientras mira el partido. Es exactamente la
 * *"cancha rotable según dónde está sentado el entrenador"* del competidor, y
 * es gratis.
 */
export function courtRows(rotation: readonly number[], flipped = false): CourtCell[][] {
  const cell = (position: number): CourtCell => ({
    position,
    number: rotation[position - 1] ?? null,
  });
  const front = [cell(4), cell(3), cell(2)];
  const back = [cell(5), cell(6), cell(1)];
  // Girar 180° = invertir el orden de las filas Y el de cada fila.
  return flipped ? [[...back].reverse(), [...front].reverse()] : [front, back];
}

// ---------------------------------------------------------------------------
// Sustituciones: la regla 15.6 convertida en opciones de pantalla
// ---------------------------------------------------------------------------

/**
 * El **jugador pareja**: con quién quedó atado un número por la regla 15.6.
 *
 * *"Un jugador sustituto puede entrar en juego una sola vez por set en lugar de
 * un jugador de la formación inicial, y sólo puede ser sustituido por el mismo
 * titular"*. Resaltarlo en la pantalla del banco no es un adorno: es la
 * diferencia entre pedir un cambio que el 2do árbitro autoriza y uno que rebota
 * delante del público.
 */
export function pairedWith(slots: readonly SubstitutionSlot[], player: number): number | null {
  for (const slot of slots) {
    if (slot.state !== 'OUT') continue;
    if (slot.starter === player) return slot.substitute;
    if (slot.substitute === player) return slot.starter;
  }
  return null;
}

/** Números que ya NO pueden entrar en este set (usaron su único ingreso). */
export function entryLocked(slots: readonly SubstitutionSlot[]): number[] {
  return slots.filter((s) => s.state === 'RETURNED').map((s) => s.substitute);
}

/** Números que ya NO pueden salir en este set (salieron y volvieron una vez). */
export function exitLocked(slots: readonly SubstitutionSlot[]): number[] {
  return slots.filter((s) => s.state === 'RETURNED').map((s) => s.starter);
}

export interface SubOption {
  /** Titular en cancha, candidato a salir. */
  playerOut: number;
  /** Quiénes pueden entrar por él. */
  candidates: number[];
  /**
   * Si la regla 15.6 ya ató esta casilla, el ÚNICO que puede entrar. La UI lo
   * resalta; es el "jugador pareja".
   */
  lockedTo: number | null;
  /** No puede salir: ya salió y volvió en este set. */
  blocked: boolean;
}

/**
 * Para cada jugador en cancha, con quién se lo puede cambiar.
 *
 * Es una función de la formación + los ciclos abiertos + el plantel, y por eso
 * se calcula acá y no en el componente: es reglamento, y el reglamento se
 * testea. Lo que la pantalla hace con esto es pintar.
 */
export function substitutionOptions(team: BenchTeamState, meta: BenchTeamMeta): SubOption[] {
  const enCancha = new Set(team.rotation.filter((n) => Number.isFinite(n)));
  const liberos = new Set([...meta.liberos, ...meta.players.filter((p) => p.libero).map((p) => p.number)]);
  const sinEntrada = new Set(entryLocked(team.slots));
  const sinSalida = new Set(exitLocked(team.slots));

  // Banco disponible: del plantel, los que no están en cancha, no son líberos
  // (el líbero NO se sustituye, se reemplaza — regla 19.3.2.1) y no agotaron su
  // único ingreso.
  const banco = meta.players
    .map((p) => p.number)
    .filter((n) => !enCancha.has(n) && !liberos.has(n) && !sinEntrada.has(n))
    .sort((a, b) => a - b);

  return team.rotation.map((playerOut) => {
    const pareja = pairedWith(team.slots, playerOut);
    return {
      playerOut,
      // Con la casilla atada, el único legal es la pareja — aunque el banco
      // esté lleno de suplentes disponibles.
      candidates: pareja !== null ? [pareja] : banco,
      lockedTo: pareja,
      blocked: sinSalida.has(playerOut),
    };
  });
}

// ---------------------------------------------------------------------------
// El estado del enlace: lo que hace cierta la promesa de §5.3
// ---------------------------------------------------------------------------

export type LinkState =
  /** Conectado y con datos frescos. */
  | 'LIVE'
  /** Se cortó, pero hace poco: se está reintentando y lo que se ve sirve. */
  | 'RECONNECTING'
  /** Se cortó hace rato: lo que se ve es viejo y hay que decirlo fuerte. */
  | 'STALE'
  /** Nunca hubo datos en esta sesión (se abrió sin red). */
  | 'COLD';

/** A partir de acá, lo que se muestra deja de poder pasar por "el partido". */
export const STALE_AFTER_MS = 25_000;

export function linkState(
  status: LiveStatus,
  lastSyncAt: number | null,
  now: number,
  staleAfterMs: number = STALE_AFTER_MS,
): LinkState {
  if (lastSyncAt === null) return status === 'open' ? 'LIVE' : 'COLD';
  const edad = now - lastSyncAt;
  if (status === 'open' && edad < staleAfterMs) return 'LIVE';
  return edad >= staleAfterMs ? 'STALE' : 'RECONNECTING';
}

/**
 * Qué pasa con un pedido que no se pudo mandar.
 *
 * `RETRYING` mientras el corte pueda ser un parpadeo; después,
 * `FALL_BACK_TO_SIGNAL`, que es la respuesta reglamentaria y no una disculpa:
 * el pedido oficial de una sustitución es el jugador entrando a la zona, y el
 * de un challenge —si la ETT no responde— la seña y la voz (VCS art. 21).
 */
export type SendOutcome = 'SENT' | 'RETRYING' | 'FALL_BACK_TO_SIGNAL';

/** Cuánto se insiste antes de mandar al DT a hacer la seña. */
export const RETRY_WINDOW_MS = 6_000;

export function sendOutcome(
  startedAt: number,
  now: number,
  retryWindowMs: number = RETRY_WINDOW_MS,
): SendOutcome {
  return now - startedAt < retryWindowMs ? 'RETRYING' : 'FALL_BACK_TO_SIGNAL';
}

// ---------------------------------------------------------------------------
// La cola, vista desde cada pantalla
// ---------------------------------------------------------------------------

/** Pendientes primero y más viejo arriba: es el orden en que hay que atender. */
export function sortQueue(intents: readonly BenchIntent[]): BenchIntent[] {
  const peso = (i: BenchIntent): number => (i.state === 'PENDING' ? 0 : 1);
  return [...intents].sort((a, b) => {
    const d = peso(a) - peso(b);
    if (d !== 0) return d;
    return a.state === 'PENDING'
      ? a.requestedAt.localeCompare(b.requestedAt)
      : b.requestedAt.localeCompare(a.requestedAt);
  });
}

/** Los pedidos que el banco ve: los suyos y nada más. */
export function ownQueue(intents: readonly BenchIntent[], team: TeamSide): BenchIntent[] {
  return sortQueue(intents.filter((i) => i.team === team));
}

export function pendingCount(intents: readonly BenchIntent[]): number {
  return intents.filter((i) => i.state === 'PENDING' && !i.stale).length;
}

/**
 * Merge del delta del poll contra lo que ya se tenía.
 *
 * El cursor viaja aparte y por eso el merge es por `id` con el `version` más
 * alto ganando: dos polls que se pisan (uno lento, uno rápido) no pueden hacer
 * retroceder un pedido ya resuelto a PENDIENTE en la pantalla.
 */
export function mergeIntents(
  current: readonly BenchIntent[],
  delta: readonly BenchIntent[],
): BenchIntent[] {
  const porId = new Map(current.map((i) => [i.id, i]));
  for (const next of delta) {
    const previo = porId.get(next.id);
    if (previo === undefined || next.version >= previo.version) porId.set(next.id, next);
  }
  return [...porId.values()];
}

// ---------------------------------------------------------------------------
// Caché local: que un F5 en medio del set no deje la tablet en blanco
// ---------------------------------------------------------------------------

export const BENCH_CACHE_PREFIX = 'scout.portal.bench.';

export function benchCacheKey(matchId: string): string {
  return `${BENCH_CACHE_PREFIX}${matchId}`;
}

export interface CachedSnapshot {
  snapshot: BenchSnapshot;
  /** Reloj del NAVEGADOR al guardar: es con lo que se calcula la antigüedad. */
  savedAt: number;
}

export function saveBenchCache(
  matchId: string,
  snapshot: BenchSnapshot,
  now: number = Date.now(),
  store: KeyValueStore = defaultKeyValueStore(),
): void {
  try {
    store.setItem(benchCacheKey(matchId), JSON.stringify({ snapshot, savedAt: now }));
  } catch {
    // Modo privado o cuota llena: se sigue sin caché. Perder el respaldo no
    // puede tirar la pantalla que está mostrando el partido.
  }
}

export function loadBenchCache(
  matchId: string,
  store: KeyValueStore = defaultKeyValueStore(),
): CachedSnapshot | null {
  try {
    const raw = store.getItem(benchCacheKey(matchId));
    if (raw === null || raw === '') return null;
    const parsed = JSON.parse(raw) as Partial<CachedSnapshot>;
    if (typeof parsed.savedAt !== 'number' || typeof parsed.snapshot !== 'object') return null;
    const snapshot = parsed.snapshot as BenchSnapshot | null;
    if (snapshot === null || typeof snapshot.matchId !== 'string') return null;
    return { snapshot, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Formatos
// ---------------------------------------------------------------------------

/** `1_845_000_000_000` → `'00:30:45'`. Mismo formato que la pestaña Clips. */
export function timecode(videoNs: number): string {
  const total = Math.max(0, Math.floor(videoNs / 1_000_000_000));
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

/** Antigüedad en segundos, redondeada hacia abajo y nunca negativa. */
export function ageSeconds(iso: string, now: number): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((now - t) / 1000));
}
